/**
 * bayesianNps.js
 *
 * Provides a Bayesian approach to estimate monthly Net Promoter Scores (NPS)
 * from 0–10 ratings. Uses a Dirichlet-multinomial model with optional
 * discounting to partially pool historical data.
 *
 * NOTE: As of the NPS refactoring, the main Bayesian calculations are now
 * performed server-side via the Supabase RPC function `calculate_bayesian_nps`.
 * The functions computeBayesianMonthlyNPS, aggregatePostsToMonthlyCounts, and
 * calculateAverageNPS are deprecated for new code but kept for backwards
 * compatibility with existing components (e.g., NPSComparisonChart.js).
 *
 * RECOMMENDED USAGE:
 *   - Use the Supabase RPC function `calculate_bayesian_nps` for NPS calculations
 *   - Use prepareChartData() and getNPSColorClass() for chart formatting
 *   - Use NPS_CONFIG for accessing configuration values
 *
 * DEPRECATED FUNCTIONS (kept for backwards compatibility):
 *   - computeBayesianMonthlyNPS
 *   - aggregatePostsToMonthlyCounts
 *   - calculateAverageNPS
 *   - gammaSample, normalSample, dirichletSample, npsFromTheta
 */

// -------------------------------------
// NPS Configuration - Centralized parameters
// -------------------------------------
export const NPS_CONFIG = {
  // Thresholds for NPS calculation
  PROMOTER_THRESHOLD: 6,  // Ratings > 6 are promoters (7-10)
  DETRACTOR_THRESHOLD: 4, // Ratings <= 4 are detractors (0-4)
  
  // Default prior parameters for each rating (0-10)
  // These values represent the "prior belief" about the distribution of ratings
  DEFAULT_PRIOR_ALPHA: [
    1.0, // rating=0
    1.0, // rating=1
    1.0, // rating=2
    1.0, // rating=3
    1.0, // rating=4

    10.0, // rating=5
    10.0, // rating=6

    8.0, // rating=7
    8.0, // rating=8
    5.0, // rating=9
    3.0  // rating=10
  ],
  
  // Scalar to adjust the strength of the prior
  // Lower values = weaker prior (data dominates faster)
  // Higher values = stronger prior (need more data to overcome prior)
  DEFAULT_PRIOR_ALPHA_SCALAR: 0.75,
  
  // Default discount factor for time series smoothing
  DEFAULT_DISCOUNT_FACTOR: 0.05,
  
  // Default number of samples for credible intervals
  DEFAULT_SAMPLES: 1000
};

// -------------------------------------
// 1. Helper: Sample from Gamma
//    (Dirichlet is basically sampling multiple Gammas and normalizing)
// -------------------------------------
function gammaSample(alpha) {
    // Simple algorithm using Marsaglia's method or use a library if you prefer.
    // This is *not* cryptographically secure—just standard random for stats.
    if (alpha < 1) {
      // Use Johnk's transformation
      const u = Math.random();
      return gammaSample(1 + alpha) * Math.pow(u, 1 / alpha);
    }
    // alpha >= 1
    const d = alpha - 1/3;
    const c = 1 / Math.sqrt(9*d);
    let x, v, u;
    while(true) {
      do {
        x = normalSample();
        v = 1 + c*x;
      } while (v <= 0);
      v = v*v*v;
      u = Math.random();
      if (u < 1 - 0.0331*(x*x)*(x*x) || Math.log(u) < 0.5*x*x + d*(1 - v + Math.log(v))) {
        return d*v;
      }
    }
  }
  
  function normalSample() {
    // Box-Muller transform
    const u1 = Math.random();
    const u2 = Math.random();
    const r = Math.sqrt(-2.0 * Math.log(u1));
    const theta = 2.0 * Math.PI * u2;
    return r * Math.cos(theta);
  }
  
  // -------------------------------------
  // 2. Helper: Sample from Dirichlet
  // -------------------------------------
  function dirichletSample(alphaArray) {
    // alphaArray = [alpha0, alpha1, ..., alpha10]
    // Return an array [theta0, ..., theta10] that sums to 1.
    const dim = alphaArray.length;
    const gammas = new Array(dim);
    let sumGamma = 0;
    for (let i = 0; i < dim; i++) {
      gammas[i] = gammaSample(alphaArray[i]);
      sumGamma += gammas[i];
    }
    // Normalize
    return gammas.map(g => g / sumGamma);
  }
  
  /**
   * Summarize one Dirichlet posterior sample into an NPS value:
   *   NPS = p(promoter) - p(detractor)
   *   where promoter = ratings > PROMOTER_THRESHOLD
   *   and detractor = ratings <= DETRACTOR_THRESHOLD
   */
  function npsFromTheta(thetaArray) {
    // Use the thresholds from the config
    const { PROMOTER_THRESHOLD, DETRACTOR_THRESHOLD } = NPS_CONFIG;

    // Calculate promoter probability (ratings > PROMOTER_THRESHOLD)
    const promoterProb = thetaArray
      .slice(PROMOTER_THRESHOLD + 1)
      .reduce((acc, val) => acc + val, 0);

    // Calculate detractor probability (ratings <= DETRACTOR_THRESHOLD)
    const detractorProb = thetaArray
      .slice(0, DETRACTOR_THRESHOLD + 1)
      .reduce((acc, val) => acc + val, 0);

    // Convert from -100 to 100 scale to 0 to 100 scale
    const rawNPS = 100 * (promoterProb - detractorProb);
    return (rawNPS + 100) / 2;
  }
  
  // -------------------------------------
  // 3. Main Function
  // -------------------------------------
  
  // Helper: Generate array of months between start and end dates
  function generateMonthRange(startMonth, endMonth) {
    const [startYear, startMo] = startMonth.split('-').map(n => parseInt(n));
    const [endYear, endMo] = endMonth.split('-').map(n => parseInt(n));
    
    const months = [];
    let currentYear = startYear;
    let currentMonth = startMo;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMo)) {
      months.push(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return months;
  }
  
  // Helper: Get current month in YYYY-MM format
  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  
  /**
   * @deprecated Use Supabase RPC function `calculate_bayesian_nps` instead.
   * This function is kept for backwards compatibility with NPSComparisonChart.js.
   *
   * @param {Object[]} monthlyData - Array of objects containing:
   *        { month: "YYYY-MM", counts: [n0, n1, ..., n10] }
   *        i.e. rating counts for each of the 11 rating values (0..10).
   *        Must be in CHRONOLOGICAL order (earliest to latest).
   *
   * @param {Number[]} [globalPriorAlpha] - 11-length array with your global prior.
   *                                        Defaults to NPS_CONFIG.DEFAULT_PRIOR_ALPHA.
   *
   * @param {Number} [discountFactor] - 0 <= discountFactor <= 1
   *        If 0, we carry over the previous month's posterior with full weight.
   *        If 1, we ignore the previous posterior and revert to global prior each month.
   *        Defaults to NPS_CONFIG.DEFAULT_DISCOUNT_FACTOR.
   *
   * @param {Number} [samples] - how many random draws to use for the credible interval.
   *        Defaults to NPS_CONFIG.DEFAULT_SAMPLES.
   *
   * @returns {Array} an array of:
   *        {
   *          month: string (YYYY-MM),
   *          meanNPS: number,
   *          lowerCI: number,
   *          upperCI: number,
   *          count: number (sum of rating counts in that month),
   *          posteriorAlpha: number[] (the Dirichlet posterior parameters for that month),
   *          isForwardFilled: boolean (whether this month was filled with previous data)
   *        }
   *
   * This is a "forward-only" method—no future data is used in earlier months' estimates.
   * Missing months will be filled with the last known values, including up to the current month.
   */
  export function computeBayesianMonthlyNPS(
    monthlyData, 
    globalPriorAlpha = NPS_CONFIG.DEFAULT_PRIOR_ALPHA, 
    discountFactor = NPS_CONFIG.DEFAULT_DISCOUNT_FACTOR, 
    samples = NPS_CONFIG.DEFAULT_SAMPLES
  ) {
    if (!monthlyData.length) return [];

    // 1) Generate complete month range up to current month
    const startMonth = monthlyData[0].month;
    const currentMonth = getCurrentMonth();
    const endMonth = monthlyData[monthlyData.length - 1].month;
    
    // If last data point is before current month, extend to current month
    const effectiveEndMonth = endMonth < currentMonth ? currentMonth : endMonth;
    const allMonths = generateMonthRange(startMonth, effectiveEndMonth);
    
    // Create a map of existing data points
    const dataMap = new Map(monthlyData.map(d => [d.month, d]));
    
    // 2) We track an evolving priorAlpha each month
    const K = globalPriorAlpha.length; // should be 11
    let prevPosteriorAlpha = globalPriorAlpha.slice();
    let lastResult = null;
    
    const results = [];

    allMonths.forEach(month => {
      const dataPoint = dataMap.get(month);
      
      if (dataPoint) {
        // Process actual data point
        const { counts } = dataPoint;
        if (!counts || counts.length !== K) {
          throw new Error(`counts must be array of length ${K}. Found: ${counts}`);
        }

        // This month's prior = mixture of the global prior & last month's posterior
        let priorAlphaM = new Array(K);
        for (let i = 0; i < K; i++) {
          priorAlphaM[i] = discountFactor * globalPriorAlpha[i] 
                           + (1 - discountFactor) * prevPosteriorAlpha[i];
        }

        // Posterior = priorAlphaM + observed counts
        let posteriorAlpha = new Array(K);
        let sumCounts = 0;
        for (let i = 0; i < K; i++) {
          posteriorAlpha[i] = priorAlphaM[i] + counts[i];
          sumCounts += counts[i];
        }

        // Sample from the Dirichlet to get credible intervals for NPS
        const npsSamples = [];
        for (let s = 0; s < samples; s++) {
          const thetaS = dirichletSample(posteriorAlpha);
          npsSamples.push(npsFromTheta(thetaS));
        }
        npsSamples.sort((a, b) => a - b);

        const meanNPS = npsSamples.reduce((acc, val) => acc + val, 0) / samples;
        const rawLowerCI = npsSamples[Math.floor(0.025 * samples)];
        const rawUpperCI = npsSamples[Math.floor(0.975 * samples)];

        // Inflation factor function - increases interval width for small sample sizes
        function inflationFactor(n) {
          const kappa = 4;  // Controls strength of inflation for small n
          const gamma = 8;  // Controls how quickly inflation drops off
          return 1 + kappa / (n + gamma);
        }

        // Compute inflated bounds while keeping mean centered
        const mid = meanNPS;
        const halfWidthLower = mid - rawLowerCI;
        const halfWidthUpper = rawUpperCI - mid;
        const factor = inflationFactor(sumCounts);

        // Apply minimum half-width of 5 (total interval width of 10)
        const minHalfWidth = 5;
        const lowerCI = mid - Math.max(factor * halfWidthLower, minHalfWidth);
        const upperCI = mid + Math.max(factor * halfWidthUpper, minHalfWidth);

        lastResult = {
          month,
          meanNPS,
          lowerCI,
          upperCI,
          count: sumCounts,
          posteriorAlpha,
          isForwardFilled: false
        };
        
        results.push(lastResult);
        prevPosteriorAlpha = posteriorAlpha;
      } else if (lastResult) {
        // Forward fill with last known values
        results.push({
          ...lastResult,
          month,
          isForwardFilled: true
        });
      }
    });

    return results;
  }
  
/**
 * Hardcoded offsets to normalize each source to Reddit's mean.
 * Positive offset = source is more negative than Reddit, shift up.
 * Negative offset = source is more positive than Reddit, shift down.
 */
const SOURCE_OFFSETS = {
  reddit: 0,
  google_app_store: -3.2,
  apple_app_store: -1.8,
  twitter: -2.0,
};

/**
 * Get the offset for a source to normalize to Reddit's mean.
 *
 * @param {String} source - The source type
 * @returns {Number} The offset to apply
 */
function getSourceOffset(source) {
  return SOURCE_OFFSETS[source] || 0;
}

/**
 * @deprecated Use Supabase RPC function `calculate_bayesian_nps` instead.
 * This function is kept for backwards compatibility with NPSComparisonChart.js.
 *
 * Aggregates posts by month and produces rating counts for NPS calculation.
 * Normalizes all sources to Reddit's mean rating distribution.
 *
 * @param {Array} posts - Array of post objects with time (unix timestamp), rating, and type properties
 * @param {Boolean} normalizeBySource - Whether to normalize to Reddit mean (default: true)
 * @returns {Array} Array of objects { month: "YYYY-MM", counts: [n0, n1, ..., n10] }
 */
export function aggregatePostsToMonthlyCounts(posts, normalizeBySource = true) {
  if (!Array.isArray(posts) || posts.length === 0) {
    return [];
  }

  try {
    // Build a map from "YYYY-MM" => array of ratings
    const countsByMonth = {};

    posts.forEach(post => {
      if (!post || typeof post.time !== 'number' || typeof post.rating !== 'number') {
        return;
      }

      const source = post.type || 'unknown';
      const offset = normalizeBySource ? getSourceOffset(source) : 0;

      // Apply offset and clamp to valid range
      let adjustedRating = post.rating + offset;
      adjustedRating = Math.max(0, Math.min(10, adjustedRating));
      const rating = Math.round(adjustedRating);

      // Convert post.time (unix) to Date object
      const postDate = new Date(post.time * 1000);

      // Get the first day of the month for this post
      const year = postDate.getUTCFullYear();
      const month = postDate.getUTCMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;

      // Initialize array for this month if it doesn't exist
      if (!countsByMonth[monthKey]) {
        countsByMonth[monthKey] = new Array(11).fill(0);
      }

      // Add the rating to the appropriate bucket
      countsByMonth[monthKey][rating] += 1;
    });

    // Turn that map into a sorted array
    const sortedMonths = Object.keys(countsByMonth).sort();
    return sortedMonths.map(m => ({
      month: m,
      counts: countsByMonth[m]
    }));
  } catch (error) {
    console.error('Error in aggregatePostsToMonthlyCounts:', error);
    return [];
  }
}

/**
 * @deprecated Period averages are now calculated from pre-computed monthly results.
 * See NPSPage.js calculatePeriodAverage() for the new approach.
 *
 * Calculate average NPS for a time period.
 * Normalizes all sources to Reddit's mean rating.
 *
 * @param {Array} posts - Array of post objects with time, rating, and type
 * @param {Date} startDate - Start date for the time period
 * @param {Date} endDate - End date for the time period
 * @param {Number} baselineNPS - Prior mean NPS (on 0-100 scale)
 * @param {Number} padding - Prior strength
 * @param {Boolean} normalizeBySource - Whether to normalize to Reddit mean (default: true)
 * @returns {Object} Object with nps, raw, noPaddingNps, and count properties
 */
export function calculateAverageNPS(posts, startDate, endDate, baselineNPS = 72, padding = 20, normalizeBySource = true) {
  if (!Array.isArray(posts) || posts.length === 0) {
    return { nps: null, raw: null, noPaddingNps: null, count: 0 };
  }

  // Filter posts within the date range
  const filteredPosts = posts.filter(post => {
    const postDate = new Date(post.time * 1000);
    return postDate >= startDate && postDate <= endDate;
  });

  if (filteredPosts.length === 0) {
    return { nps: null, raw: null, noPaddingNps: null, count: 0 };
  }

  // Apply normalization to ratings
  const normalizedPosts = filteredPosts.map(post => {
    const source = post.type || 'unknown';
    const offset = normalizeBySource ? getSourceOffset(source) : 0;
    let adjustedRating = post.rating + offset;
    adjustedRating = Math.max(0, Math.min(10, adjustedRating));
    return { ...post, rating: adjustedRating };
  });

  const { PROMOTER_THRESHOLD, DETRACTOR_THRESHOLD } = NPS_CONFIG;

  // Calculate metrics using normalized ratings
  const totalRating = normalizedPosts.reduce((sum, post) => sum + post.rating, 0);
  const rawAverage = totalRating / normalizedPosts.length;

  const promoters = normalizedPosts.filter(post => post.rating > PROMOTER_THRESHOLD).length;
  const detractors = normalizedPosts.filter(post => post.rating <= DETRACTOR_THRESHOLD).length;

  const promoterRate = promoters / normalizedPosts.length;
  const detractorRate = detractors / normalizedPosts.length;

  // Calculate raw NPS (convert to 0-100 scale)
  const rawNPS = (promoterRate - detractorRate) * 100;
  const scaledRawNPS = (rawNPS + 100) / 2;

  // Calculate adjusted NPS with prior (baselineNPS is now on 0-100 scale)
  // Convert baseline back to -100 to 100 for calculation
  const baselineOldScale = (baselineNPS * 2) - 100;
  const effectiveCount = normalizedPosts.length;
  const adjustedPromoterRate = (promoterRate * effectiveCount + (baselineOldScale/100 + 0.5) * padding / 2) / (effectiveCount + padding / 2);
  const adjustedDetractorRate = (detractorRate * effectiveCount + (0.5 - baselineOldScale/100) * padding / 2) / (effectiveCount + padding / 2);

  const adjustedNPSOldScale = (adjustedPromoterRate - adjustedDetractorRate) * 100;
  const adjustedNPS = (adjustedNPSOldScale + 100) / 2;

  return {
    nps: Math.round(adjustedNPS),
    raw: rawAverage,
    noPaddingNps: Math.round(scaledRawNPS),
    count: filteredPosts.length
  };
}

/**
 * Get the CSS color class based on NPS score
 * 
 * @param {Number} nps - The NPS score
 * @returns {String} CSS class name for the score
 */
export function getNPSColorClass(nps) {
  if (nps >= 70) return 'positive';
  if (nps < 50) return 'negative';
  return 'neutral';
}

/**
 * Prepare chart data from NPS results
 * 
 * @param {Array} npsResults - Results from computeBayesianMonthlyNPS
 * @param {String} productName - Name of the product
 * @param {Boolean} showConfidenceIntervals - Whether to show confidence intervals
 * @param {Object} chartColors - Object with color settings
 * @returns {Object} Chart data object for Chart.js
 */
export function prepareChartData(npsResults, productName, showConfidenceIntervals = false, chartColors = {}) {
  if (!npsResults || npsResults.length === 0) {
    return null;
  }
  
  const labels = npsResults.map(r => r.month);
  
  const mainDataset = {
    label: productName,
    data: npsResults.map(r => ({
      x: r.month,
      y: r.meanNPS,
      confidenceBounds: {
        lower: r.lowerCI,
        upper: r.upperCI
      },
      count: r.count,
      isForwardFilled: r.isForwardFilled
    })),
    borderColor: chartColors.main || 'rgb(47, 71, 184)',
    backgroundColor: 'rgba(47, 71, 184, 0.5)',
    borderWidth: 3,
    tension: 0.1,
    pointRadius: 4,
    pointHoverRadius: 6,
  };
  
  const datasets = [mainDataset];
  
  // Add confidence intervals if requested
  if (showConfidenceIntervals) {
    // Add upper confidence bound
    datasets.push({
      label: `${productName} Upper Confidence Band`,
      data: npsResults.map(r => ({
        x: r.month,
        y: r.upperCI
      })),
      borderColor: 'rgba(47, 71, 184, 0.3)',
      borderWidth: 1,
      borderDash: [5, 5],
      pointRadius: 0,
      fill: false,
      tension: 0.1
    });
    
    // Add lower confidence bound
    datasets.push({
      label: `${productName} Lower Confidence Band`,
      data: npsResults.map(r => ({
        x: r.month,
        y: r.lowerCI
      })),
      borderColor: 'rgba(47, 71, 184, 0.3)',
      borderWidth: 1,
      borderDash: [5, 5],
      pointRadius: 0,
      fill: '-1',
      backgroundColor: 'rgba(47, 71, 184, 0.1)',
      tension: 0.1
    });
  }
  
  return {
    labels,
    datasets
  };
}
  